import React from "react";

export function StatusPill({ tone = "neutral", children, testid }) {
  return (
    <span className={`ach-pill pill-${tone}`} data-testid={testid}>{children}</span>
  );
}

export function KpiCard({ tone = "neutral", number, label, testid, onClick }) {
  // When onClick is provided the card becomes an interactive tile:
  //   - proper button semantics (role=button, tabIndex=0) for keyboard/AT users
  //   - Enter and Space trigger the same onClick
  //   - cursor:pointer so the eye and the finger agree
  // When onClick is omitted (e.g. kpi-money-owed) the card stays inert —
  // a plain DIV with no role, no tabIndex, no cursor change.
  const interactive = typeof onClick === "function";
  const onKeyDown = (e) => {
    if (!interactive) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick(e);
    }
  };
  return (
    <div
      className={`ach-kpi tone-${tone}${interactive ? " ach-kpi--clickable" : ""}`}
      data-testid={testid}
      onClick={interactive ? onClick : undefined}
      onKeyDown={interactive ? onKeyDown : undefined}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      style={interactive ? { cursor: "pointer" } : undefined}
    >
      <div className="ach-kpi__bar" />
      <div className="ach-kpi__num">{number}</div>
      <div className="ach-kpi__label">{label}</div>
    </div>
  );
}

export function AlertRow({ tone, icon, title, sub, actionText, onAction, testid }) {
  const glyph = icon || (tone === "green" ? "✓" : "!");
  return (
    <div className="ach-alert" data-testid={testid}>
      <span className={`ach-alert__chip tone-${tone}`}>{glyph}</span>
      <div className="ach-alert__body">
        <div className="ach-alert__title">{title}</div>
        {sub && <div className="ach-alert__sub">{sub}</div>}
      </div>
      {actionText && (
        <a href="#" className="ach-alert__action" onClick={(e)=>{e.preventDefault(); onAction && onAction();}}>{actionText}</a>
      )}
    </div>
  );
}

/**
 * Returns handlers for a modal-backdrop element that only dismiss the modal on
 * a genuine backdrop click (mousedown started AND mouseup ended on the backdrop
 * itself, not on a child input where the user was trying to select text).
 *
 * If `isDirty` is true, backdrop clicks are intercepted and the user is prompted
 * to confirm losing their unsaved changes — no more silent data loss when
 * someone brushes past the edge of an edit dialog.
 *
 *   const dismiss = useBackdropDismiss(isDirty, onClose);
 *   <div className="ach-modal-backdrop" {...dismiss}>
 *
 * Note: this is the ONE place that governs backdrop-dismissal behaviour across
 * every edit modal in the app. If Todd wants to change how "unsaved changes"
 * confirmation reads, edit the message here.
 */
export function useBackdropDismiss(isDirty, onClose) {
  return {
    onMouseDown: (e) => {
      if (e.target === e.currentTarget) e.currentTarget.dataset.down = "1";
    },
    onMouseUp: (e) => {
      const wasBackdropClick = e.target === e.currentTarget && e.currentTarget.dataset.down === "1";
      e.currentTarget.dataset.down = "";
      if (!wasBackdropClick) return;
      if (isDirty && !window.confirm("Discard the changes you just typed?")) return;
      onClose();
    },
  };
}

/* A table action cell. Keeps the <td> a real table cell and does the flexing on
   an inner wrapper, so the row grows with its buttons instead of the buttons
   overflowing it. Shared by Hires and Quotes so the responsive behaviour lives
   in one place. */
export function RowActions({ children, ...rest }) {
  return (
    <td className="ach-row-actions" {...rest}>
      <div className="ach-row-actions__inner">{children}</div>
    </td>
  );
}
